/**
 * Header-tree node discrimination, shared by the grid's column builder (`useHeaderTreeColumns`) and the
 * Excel export reader (`aurigaExportData`), so both classify a node the same way.
 */
import { HeaderNodeKind } from '@services/api/deckManagement/graphql/schema/API';
import { BlockNode, GroupNode, HeaderNode, LeafNode } from 'types/Page.types';

/**
 * How a header-tree node is discriminated.
 *
 * `__typename` is the discriminator, and it is reliable because `scripts/addTypenameToUnions.js`
 * injects `__typename` onto every union selection as a post-codegen step (wired into
 * `npm run amplify:gen:*`). Amplify's raw codegen output does NOT — it emits `__typename` only
 * inside nested object selections, never on a union's own inline fragments — so before that step a
 * node arrived with `__typename === undefined` at runtime while `API.ts` typed it as a required
 * literal. That combination compiles, typechecks, and silently matches nothing.
 *
 * `kind` (HeaderNodeKind) is selected on every node too, so it serves as an independent fallback for
 * a tree supplied from a thinner selection set. It is typed nullable, which is why it is the
 * fallback rather than the primary.
 */
export type HeaderNodeShape = 'GROUP' | 'BLOCK' | 'LEAF';

/**
 * Resolves which kind of header node this is.
 *
 * @param node a node from a section's `columnHeaderTree`
 * @returns 'GROUP' | 'BLOCK' | 'LEAF', or `null` when neither discriminator resolves
 */
export const resolveNodeShape = (node: HeaderNode): HeaderNodeShape | null => {
  // Read both discriminators off a widened view. `API.ts` types `__typename` as a REQUIRED literal,
  // so TypeScript treats the first switch below as exhaustive and narrows `node` to `never`
  // afterwards — which would make the `kind` fallback statically unreachable. At runtime
  // `__typename` may well be absent, so the fallback has to stay reachable.
  const { __typename: typename, kind } = node as { __typename?: string; kind?: HeaderNodeKind | null };

  switch (typename) {
    case 'GroupNode':
      return 'GROUP';
    case 'BlockNode':
      return 'BLOCK';
    case 'LeafNode':
      return 'LEAF';
    default:
      break;
  }

  // Fallback for trees fetched with a selection set that omits `__typename`. SECTION is not a
  // member of the output HeaderRootNode union today, but it is structurally a grouping band, so
  // map it to GROUP rather than dropping the subtree should the BE ever widen the union.
  switch (kind) {
    case HeaderNodeKind.GROUP:
    case HeaderNodeKind.SECTION:
      return 'GROUP';
    case HeaderNodeKind.BLOCK:
      return 'BLOCK';
    case HeaderNodeKind.LEAF:
      return 'LEAF';
    default:
      return null;
  }
};

/** Whether the node is a LEAF (a data column). */
export const isLeafNode = (node: HeaderNode): node is LeafNode => resolveNodeShape(node) === 'LEAF';
/** Whether the node is a BLOCK (a group of columns). */
export const isBlockNode = (node: HeaderNode): node is BlockNode => resolveNodeShape(node) === 'BLOCK';
/** Whether the node is a GROUP (a band over blocks). */
export const isGroupNode = (node: HeaderNode): node is GroupNode => resolveNodeShape(node) === 'GROUP';
